/**
 * Klavox Send (`R15`) : partage PONCTUEL d'un secret, sans compte côté
 * destinataire, et sans que le serveur puisse lire quoi que ce soit.
 *
 * ```
 * sendSecret   = 256 bits du CSPRNG -> FRAGMENT `#` de l'URL, jamais transmis au serveur
 * sendSalt     = 128 bits du CSPRNG -> metadonnee PUBLIQUE, stockee a cote du ciphertext
 * passwordKey  = mot de passe ? Argon2id(motDePasse, SHA-256(domaine || sendSalt)) : (rien)
 * sendKey      = HKDF-SHA256(ikm = sendSecret || passwordKey, salt = sendSalt, info = "…|send-key")
 * ciphertext   = seal(sendKey, JSON(payload), contexte = "…|send", AAD = sendId)
 * ```
 *
 * Deux invariants de conception, tous deux vérifiés par `test/send.test.ts` :
 *
 *  1. **ET, jamais OU.** Quand un mot de passe est posé, il entre dans la MÊME
 *     dérivation que le secret d'URL : le lien seul ne déchiffre rien, le mot
 *     de passe seul non plus. C'est la différence avec une autorisation
 *     vérifiée par le serveur (un serveur compromis la contournerait) —
 *     ici, un serveur malveillant ne gagne rien à mentir.
 *  2. **Le serveur ne voit jamais la clé.** Il stocke `ciphertext`, `sendSalt`
 *     et les paramètres Argon2id ; l'expiration et le nombre de vues qu'il
 *     applique sont une commodité, pas la protection. La protection est le
 *     fragment d'URL, qu'il n'a jamais.
 *
 * ⚠️ Limite assumée : le destinataire d'un envoi peut toujours recopier le
 * contenu déchiffré. « Nombre de vues » borne les TÉLÉCHARGEMENTS, pas la
 * mémoire de celui qui a lu — aucun coffre au monde ne sait faire mieux.
 */
import { argon2id, argon2idAsync } from '@noble/hashes/argon2.js';
import { hkdf } from '@noble/hashes/hkdf.js';
import { sha256 } from '@noble/hashes/sha2.js';
import {
  KDF_DOMAIN,
  SEAL_CONTEXT,
  SEND_SALT_LENGTH,
  SEND_SECRET_LENGTH,
  SYMMETRIC_KEY_LENGTH,
  type AeadAlgorithmId,
  type KdfParams,
} from './constants.js';
import { openFromString, sealToString, type SealedString } from './aead.js';
import {
  assertBytes,
  base64UrlToBytes,
  bytesToBase64Url,
  bytesToUtf8,
  concatBytes,
  utf8ToBytes,
  wipe,
} from './bytes.js';
import { InvalidParameterError } from './errors.js';
import { assertKdfParams, normalizeMasterPassword } from './kdf.js';
import { SecretKey, assertSecretKey } from './secret.js';
import { randomBytes } from './random.js';

/**
 * Un champ d'une fiche partagée (`kind: 'item'`, « Share item » de 1Password) :
 * libellé, valeur, et s'il est masqué à l'ouverture chez le destinataire.
 */
export interface SendItemField {
  readonly name: string;
  readonly value: string;
  readonly secret?: boolean;
}

/** Borne du nombre de champs d'une fiche partagée (garde-fou de forme, pas une limite d'usage). */
export const MAX_SEND_ITEM_FIELDS = 200;

/** Contenu d'un envoi. Tout est chiffré — y compris le nom du fichier et le libellé. */
export interface SendPayload {
  readonly kind: 'text' | 'file' | 'item';
  /** Intitulé montré au destinataire APRÈS déchiffrement (jamais côté serveur). */
  readonly label?: string;
  /** Contenu texte (`kind: 'text'`). */
  readonly text?: string;
  /**
   * Texte masqué à l'ouverture (« Hide text by default » de Bitwarden Send) :
   * le destinataire doit cliquer « Afficher ». Scellé avec le reste — le
   * serveur ne sait même pas que l'option existe.
   */
  readonly hidden?: boolean;
  /** Nom du fichier (`kind: 'file'`). */
  readonly fileName?: string;
  readonly mimeType?: string;
  /** Contenu du fichier en base64 (`kind: 'file'`). */
  readonly dataBase64?: string;
  /** Champs de la fiche partagée (`kind: 'item'`), dans l'ordre de la fiche. */
  readonly fields?: readonly SendItemField[];
  readonly [key: string]: unknown;
}

/** Génère le secret d'URL d'un envoi (256 bits, CSPRNG). */
export function generateSendSecret(): SecretKey {
  return SecretKey.random(SEND_SECRET_LENGTH, 'sendSecret');
}

/** Génère le sel public d'un envoi (128 bits, CSPRNG). Non secret. */
export function generateSendSalt(): Uint8Array {
  return randomBytes(SEND_SALT_LENGTH);
}

/**
 * Encode le secret pour le fragment d'URL : base64url, sans remplissage ni
 * caractère à échapper — une URL recopiée à la main reste valide.
 */
export function formatSendSecret(secret: SecretKey): string {
  assertSecretKey(secret, 'secret', SEND_SECRET_LENGTH);
  // Remplissage `=` retire : certains clients de messagerie tronquent une URL
  // au premier caractere « bizarre », et un fragment mutile = envoi perdu.
  return bytesToBase64Url(secret.reveal()).replace(/=+$/u, '');
}

/**
 * Relit le secret d'un fragment d'URL.
 *
 * @throws {InvalidParameterError} base64url invalide ou longueur inattendue
 */
export function parseSendSecret(value: string): SecretKey {
  if (typeof value !== 'string') {
    throw new InvalidParameterError('sendSecret: chaine base64url attendue');
  }
  // Remplissage remis : `formatSendSecret` l'enleve, le decodeur l'exige.
  const padded = value + '='.repeat((4 - (value.length % 4)) % 4);
  const bytes = base64UrlToBytes(padded);
  if (bytes.length !== SEND_SECRET_LENGTH) {
    wipe(bytes);
    throw new InvalidParameterError(
      `sendSecret: ${SEND_SECRET_LENGTH} octets attendus, ${bytes.length} recus`,
    );
  }
  return SecretKey.adopt(bytes, 'sendSecret', SEND_SECRET_LENGTH);
}

export interface DeriveSendKeyInput {
  /** Secret du fragment d'URL. */
  readonly secret: SecretKey;
  /** Sel public de l'envoi. */
  readonly salt: Uint8Array;
  /** Mot de passe OPTIONNEL. Absent : le lien suffit. Présent : il est EXIGÉ en plus du lien. */
  readonly password?: string;
  /** Paramètres Argon2id du mot de passe (métadonnée publique de l'envoi). */
  readonly kdfParams?: KdfParams;
}

/** Sel Argon2id du mot de passe = `SHA-256(domaine || sendSalt)`, soit 32 octets. */
function sendPasswordSalt(salt: Uint8Array): Uint8Array {
  return sha256(concatBytes(utf8ToBytes(KDF_DOMAIN.sendPasswordSalt), salt));
}

function prepareSend(input: DeriveSendKeyInput): {
  secret: Uint8Array;
  salt: Uint8Array;
  password: Uint8Array | null;
  passwordSalt: Uint8Array;
  params: KdfParams;
} {
  if (input === null || typeof input !== 'object') {
    throw new InvalidParameterError('input: objet attendu');
  }
  assertSecretKey(input.secret, 'secret', SEND_SECRET_LENGTH);
  assertBytes(input.salt, 'salt', SEND_SALT_LENGTH);
  const hasPassword = input.password !== undefined && input.password !== '';
  if (hasPassword && input.kdfParams === undefined) {
    throw new InvalidParameterError('kdfParams: obligatoires des qu’un mot de passe protege l’envoi');
  }
  const params = input.kdfParams ?? { algorithm: 'argon2id', memoryKiB: 0, iterations: 0, parallelism: 0 };
  if (hasPassword) assertKdfParams(params);
  return {
    secret: input.secret.reveal(),
    salt: input.salt,
    password: hasPassword ? utf8ToBytes(normalizeMasterPassword(input.password!)) : null,
    passwordSalt: sendPasswordSalt(input.salt),
    params,
  };
}

/**
 * `sendKey = HKDF-SHA256(sendSecret || passwordKey, salt = sendSalt)`.
 *
 * Le HKDF prend les DEUX facteurs en une seule extraction : retirer l'un
 * change la clé, donc aucun des deux ne suffit. Sans mot de passe, la partie
 * `passwordKey` est vide et le secret d'URL est seul maître — c'est le mode
 * par défaut, et 256 bits de CSPRNG rendent la recherche exhaustive vaine.
 */
export function deriveSendKey(input: DeriveSendKeyInput): SecretKey {
  const { secret, salt, password, passwordSalt, params } = prepareSend(input);
  let passwordKey: Uint8Array | null = null;
  try {
    if (password !== null) {
      passwordKey = argon2id(password, passwordSalt, {
        t: params.iterations,
        m: params.memoryKiB,
        p: params.parallelism,
        dkLen: SYMMETRIC_KEY_LENGTH,
      });
    }
    return expandSendKey(secret, passwordKey, salt);
  } finally {
    wipe(password, passwordKey);
  }
}

/** Variante asynchrone : ne fige pas l'interface pendant Argon2id. */
export async function deriveSendKeyAsync(input: DeriveSendKeyInput): Promise<SecretKey> {
  const { secret, salt, password, passwordSalt, params } = prepareSend(input);
  let passwordKey: Uint8Array | null = null;
  try {
    if (password !== null) {
      passwordKey = await argon2idAsync(password, passwordSalt, {
        t: params.iterations,
        m: params.memoryKiB,
        p: params.parallelism,
        dkLen: SYMMETRIC_KEY_LENGTH,
      });
    }
    return expandSendKey(secret, passwordKey, salt);
  } finally {
    wipe(password, passwordKey);
  }
}

function expandSendKey(secret: Uint8Array, passwordKey: Uint8Array | null, salt: Uint8Array): SecretKey {
  const ikm = passwordKey === null ? secret : concatBytes(secret, passwordKey);
  try {
    return SecretKey.adopt(
      hkdf(sha256, ikm, salt, utf8ToBytes(KDF_DOMAIN.sendKey), SYMMETRIC_KEY_LENGTH),
      'sendKey',
      SYMMETRIC_KEY_LENGTH,
    );
  } finally {
    // `ikm` est une COPIE quand les deux facteurs sont concatenes : on l'efface.
    // Sinon c'est la vue du `SecretKey` de l'appelant -- surtout pas.
    if (passwordKey !== null) wipe(ikm);
  }
}

function sendAad(sendId: string): Uint8Array {
  if (typeof sendId !== 'string' || sendId.length === 0) {
    throw new InvalidParameterError('sendId: chaine non vide attendue');
  }
  return utf8ToBytes(sendId);
}

export interface SealSendInput {
  readonly sendKey: SecretKey;
  /** Identifiant public de l'envoi, lié en AAD : un serveur ne peut pas permuter deux envois. */
  readonly sendId: string;
  readonly payload: SendPayload;
  readonly algorithm?: AeadAlgorithmId;
}

/** Scelle le contenu d'un envoi (nonce frais). */
export function sealSend(input: SealSendInput): SealedString {
  assertSecretKey(input?.sendKey, 'sendKey', SYMMETRIC_KEY_LENGTH);
  if (input.payload === null || typeof input.payload !== 'object') {
    throw new InvalidParameterError('payload: objet attendu');
  }
  const json = JSON.stringify(input.payload);
  if (typeof json !== 'string') {
    throw new InvalidParameterError('payload: valeur non serialisable en JSON');
  }
  const plaintext = utf8ToBytes(json);
  try {
    return sealToString(input.sendKey, plaintext, {
      context: SEAL_CONTEXT.send,
      associatedData: sendAad(input.sendId),
      algorithm: input.algorithm,
    });
  } finally {
    wipe(plaintext);
  }
}

/** Forme des champs d'une fiche partagée : rien d'autre que des chaînes et un drapeau, jamais vide. */
function assertSendItemFields(fields: unknown): void {
  if (!Array.isArray(fields) || fields.length === 0 || fields.length > MAX_SEND_ITEM_FIELDS) {
    throw new InvalidParameterError('envoi dechiffre: champs de fiche attendus');
  }
  for (const field of fields as unknown[]) {
    const ok =
      field !== null &&
      typeof field === 'object' &&
      typeof (field as SendItemField).name === 'string' &&
      typeof (field as SendItemField).value === 'string' &&
      ((field as SendItemField).secret === undefined || typeof (field as SendItemField).secret === 'boolean');
    if (!ok) throw new InvalidParameterError('envoi dechiffre: champ de fiche non conforme');
  }
}

export interface OpenSendInput {
  readonly sendKey: SecretKey;
  readonly sendId: string;
  readonly ciphertext: SealedString;
}

/**
 * Ouvre le contenu d'un envoi.
 *
 * @throws {DecryptionFailedError} mauvais lien, mauvais mot de passe, blob
 *   altéré, ou `sendId` non concordant — les quatre sont indistinguables, pas
 *   d'oracle exploitable pour deviner le mot de passe.
 */
export function openSend(input: OpenSendInput): SendPayload {
  assertSecretKey(input?.sendKey, 'sendKey', SYMMETRIC_KEY_LENGTH);
  const plaintext = openFromString(input.sendKey, input.ciphertext, {
    context: SEAL_CONTEXT.send,
    associatedData: sendAad(input.sendId),
  });
  try {
    const payload = JSON.parse(bytesToUtf8(plaintext)) as SendPayload;
    if (payload === null || typeof payload !== 'object' || Array.isArray(payload)) {
      throw new InvalidParameterError('envoi dechiffre: objet attendu');
    }
    if (payload.kind !== 'text' && payload.kind !== 'file' && payload.kind !== 'item') {
      throw new InvalidParameterError('envoi dechiffre: kind inconnu');
    }
    if (payload.kind === 'item') assertSendItemFields(payload.fields);
    return payload;
  } catch (cause) {
    if (cause instanceof InvalidParameterError) throw cause;
    throw new InvalidParameterError('contenu dechiffre non conforme a un envoi Klavox');
  } finally {
    wipe(plaintext);
  }
}
